import React, { Component, useEffect, useMemo, useState } from 'react'
import { useGetAllCampaignsByDealership } from '../../../../api/dealership/campaigns/useGetAllCampaignsByDealership'
import {
  Button,
  CardRadioInput,
  DropdownButton,
  Info,
  LoadingComponent,
  Pill,
  Table,
  Tooltip,
} from '@demandlocal/components'
import { DateRange } from '../../../../lib/types/DateRange'
import Select, { CSSObjectWithLabel } from 'react-select'
import { match } from 'ts-pattern'
import dayjs from 'dayjs'
import { createColumnHelper } from '@tanstack/react-table'
import { Campaign } from '../../../../lib/types/Campaign'
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { lowerCase } from 'lodash'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { useGetCustomerSourcesByDealershipID } from '../../../../api/dealership/customers/sources/useGetCustomerSourcesByDealershipID'
import CampaignBreakdownReportForm, {
  METRICS,
} from '../../../../forms/Campaigns/BreakdownReportForm'
import { HtmlPortalNode } from 'react-reverse-portal'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import { useGetDealershipConversionGoalsByDealershipID } from '../../../../api/dealership/audiences/useGetConversionGoalsByDealershipID'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { GA4 } from '../../../../lib/utils/ga4'
import mtm from '../../../../lib/utils/mtm'
import { usePostCampaignQuickReportGenerator } from '../../../../api/dealership/campaigns/usePostCampaignQuickReportGenerator'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../../store/selectors/userSelector'
import { useGetCampaignsSummaryList } from '../../../../api/dealership/campaigns/useGetCampaignsSummaryList'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { ReactComponent as IconImport } from '../../../../images/icons/icon_import_large.svg'
import { ReactComponent as IconCreate } from '../../../../images/icons/icon_create_large.svg'
import { ReactComponent as IconGoogleAds } from '../../../../images/vendors/google_ads_logo.svg'
import { ReactComponent as IconFacebookAds } from '../../../../images/vendors/meta_logo.svg'

const columnHelper = createColumnHelper<Campaign>()

const CampaignBreakdownReport = ({
  reportsPortalNode,
}: {
  reportsPortalNode: HtmlPortalNode<Component<unknown>>
}): JSX.Element => {
  const dealership = useDealership()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: tag_names } = useGetDealershipConversionGoalsByDealershipID()
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()
  const { mutateAsync } = usePostCampaignQuickReportGenerator()
  const user = useSelector(currentUserSelector)

  const [dateFilter, setDateFilter] = React.useState<{
    label: string
    value: string
  }>({
    label: `last ${searchParams.get('dateFilter') === '6_month' ? '6' : '3'} months`,
    value: searchParams.get('dateFilter') || '3_month',
  })
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().startOf('month').subtract(3, 'month').format('YYYY-MM-DD'),
      endDate: dayjs()
        .startOf('month')
        .subtract(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD'),
    },
  })

  const { data: campaigns, isLoading: isLoadingCampaigns } =
    useGetAllCampaignsByDealership({
      filters: {
        ...filters.dateRange,
        onlyFlights: 1,
      },
    })
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)

  const uniqueCampaigns = useMemo(() => {
    const groupedMap = new Map()
    campaigns?.data?.forEach((obj) => {
      const key = obj.client_campaign_id
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          client_campaign_id: key,
          campaign_name: obj.client_campaign_display_name,
          product_name: obj.product_name,
          product_code: obj.product_code,
          label: obj.client_campaign_display_name,
          value: key,
        })
      }
    })
    return Array.from(groupedMap.values())
  }, [campaigns])

  const { data: sources, isLoading: isLoadingSources } =
    useGetCustomerSourcesByDealershipID({
      pageIndex: 1,
      pageSize: 10,
      sorting: [{ id: 'updated_date', sort: 'desc' }],
    })

  const hasDMS = useMemo(() => {
    return sources?.data?.some(
      (s) => s.data_provider !== 'Manual Upload' && s.customer_source_type === 'Sales',
    )
  }, [sources])

  useEffect(() => {
    const date = match(dateFilter.value)
      .with('3_month', () => ({
        start_date: dayjs().startOf('month').subtract(3, 'month').format('YYYY-MM-DD'),
        end_date: dayjs()
          .startOf('month')
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD'),
      }))
      .with('6_month', () => ({
        start_date: dayjs().startOf('month').subtract(6, 'month').format('YYYY-MM-DD'),
        end_date: dayjs()
          .startOf('month')
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD'),
      }))
      .otherwise(() => ({ start_date: '', end_date: '' }))

    setFilters({
      dateRange: {
        startDate: date.start_date,
        endDate: date.end_date,
      },
    })
  }, [dateFilter.value, setFilters])

  useEffect(() => {
    setSearchParams((sp) => {
      sp.set('dateFilter', dateFilter?.value)
      return sp
    })
  }, [dateFilter.value, setSearchParams])

  useEffect(() => {
    if (!isLoadingCampaigns) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingCampaigns])

  const baseColumns = useMemo(
    () => [
      columnHelper.accessor('start_date', {
        header: 'Booking Month',
        cell: (info) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={`../${lowerCase(info.row.original.product_code)}/${
              info.row.original.client_campaign_id
            }/?startDate=${dayjs(info.row.original.start_date).format(
              'YYYY-MM-DD',
            )}&endDate=${dayjs(info.row.original.end_date).format('YYYY-MM-DD')}`}>
            {dayjs(info.getValue()).format("MMMM 'YY") || '-'}
          </Link>
        ),
        footer: ({ table }) =>
          `Total for ${table.getFilteredRowModel().rows.length} campaigns`,
      }),
      columnHelper.display({
        id: 'flight_dates',
        header: 'Dates',
        cell: ({ row }) => (
          <span>
            {dayjs(row.original.start_date).format('M/D/YY')} -{' '}
            {dayjs(row.original.end_date).format('M/D/YY')}
          </span>
        ),
      }),
      ...(Boolean(agency?.show_budget_type)
        ? [
            columnHelper.display({
              header: 'Budget',
              id: 'budget',
              cell: ({ row }) =>
                agency?.show_budget_type === 1
                  ? formatCurrency(row.original.demand_local_net)
                  : formatCurrency(row.original.budget),
            }),
          ]
        : []),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) => total + Number(row.getValue('impressions')),
                0,
              ),
          ),
      }),
    ],
    [agency?.show_budget_type],
  )

  const conversionColumns = useMemo(() => {
    if (tag_names) {
      return tag_names.map((tag) => {
        return columnHelper.display({
          header: ({ table }) => {
            const isSearch = ['MSE', 'SEM', 'INV', 'TRV', 'VLA'].includes(
              table?.getRowModel()?.rows[0]?.original?.product_code,
            )
            return (
              <div className="flex flex-row gap-1 items-center">
                {tag.tag_name}
                {isSearch ? (
                  <Info message={`${tag.tag_name} sourced from Google Ads`} />
                ) : (
                  <Info
                    message={`Number of visits where the ${dealership?.attribution_tracking?.type.replace(
                      '_',
                      ' ',
                    )} fell within the flight date. ${
                      dealership?.attribution_tracking?.type !== 'conversion'
                        ? 'This will not match numbers in campaign report, as those are based off conversion date'
                        : ''
                    }`}
                  />
                )}
              </div>
            )
          },
          id: tag.tag_name,
          cell: (info) =>
            info.row.original[tag.tag_name] ||
            (['MSE', 'SEM', 'VLA', 'TRV'].includes(info.row.original.product_code) ? (
              <span>N/A</span>
            ) : (
              <Tooltip label={'Site attribution not configured for this campaign'}>
                <span>N/A</span>
              </Tooltip>
            )),
        })
      })
    } else {
      return []
    }
  }, [dealership?.attribution_tracking?.type, tag_names])

  const audienceColumns = useMemo(() => {
    return [
      ...baseColumns,
      columnHelper.accessor('audience_impressions', {
        header: 'Audience impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) => total + Number(row.getValue('audience_impressions')),
                0,
              ),
          ),
      }),
    ]
  }, [baseColumns])

  const attributedSales = useMemo(() => {
    return [
      columnHelper.accessor('sales', {
        header: 'Attributed sales',
        cell: (info) =>
          info.getValue() ||
          (!hasDMS ? <Info message={'Connect your DMS to see attributed sales'} /> : 0),
      }),
    ]
  }, [hasDMS])

  const videoColumns = useMemo(() => {
    return [
      ...baseColumns,
      columnHelper.accessor('views', {
        header: 'Views',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('views')), 0),
          ),
      }),
      columnHelper.display({
        id: 'vtr',
        header: 'VTR',
        cell: ({ row }) =>
          `${
            Math.round(((row.original.views || 0) / row.original.impressions) * 10000) /
            100
          }%`,
      }),
    ]
  }, [baseColumns])

  const displayColumns = useMemo(() => {
    return [
      ...baseColumns,
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => formatNumber(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('clicks')), 0),
          ),
      }),
      columnHelper.display({
        id: 'ctr',
        header: 'CTR',
        cell: ({ row }) =>
          `${
            Math.round(((row.original.clicks || 0) / row.original.impressions) * 10000) /
            100
          }%`,
      }),
    ]
  }, [baseColumns])

  const displayWithSalesColumns = useMemo(() => {
    return [...displayColumns, ...attributedSales]
  }, [attributedSales, displayColumns])

  const searchColumns = useMemo(() => {
    return [
      ...displayColumns,
      columnHelper.accessor('calls', {
        header: 'Phone Calls',
        cell: (info) => info.getValue() || 0,
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('calls')), 0),
          ),
      }),
      ...conversionColumns,
    ]
  }, [conversionColumns, displayColumns])

  const facebookLeadsColumns = useMemo(() => {
    return [
      ...displayColumns,
      columnHelper.accessor('leads', {
        header: 'Leads',
        cell: (info) => info.getValue(),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('leads')), 0),
          ),
      }),
    ]
  }, [displayColumns])

  const leadsWithSalesColumns = useMemo(() => {
    return [...facebookLeadsColumns, ...attributedSales]
  }, [attributedSales, facebookLeadsColumns])

  const mobileColumns = useMemo(() => {
    return [
      ...displayColumns,
      columnHelper.accessor('rsv', {
        header: 'Walk Ins',
        cell: (info) => info.getValue(),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('rsv')), 0),
          ),
      }),
    ]
  }, [displayColumns])

  const hasCampaigns = useMemo(
    () => uniqueCampaigns?.length !== 0,
    [uniqueCampaigns.length],
  )

  const navigate = useNavigate()
  const formContext = useForm<FieldValues>()
  const {
    handleSubmit,
    formState: { isDirty },
  } = formContext

  const onSubmit = (values: Partial<FieldValues>): void => {
    const { create_campaign_type } = values
    if (create_campaign_type) navigate(`../list/create/${create_campaign_type}`)
  }

  return (
    <>
      <div className={'flex flex-col gap-12 pb-8'} id={'content'}>
        <div className={'flex flex-col'}>
          <span className="text-md pb-5">
            Analyze trends and performance metrics with a rolling 3-month campaign
            comparison.
          </span>
          <CampaignBreakdownReportForm portalNode={reportsPortalNode} />
          <div className="flex flex-row items-center justify-between mb-6">
            <span className={'font-bold text-primary-xiketic text-sm'}>
              Month over Month Campaigns
            </span>
            <div className={'flex flex-row gap-4'}>
              <div className={'flex flex-row gap-2 items-center'}>
                <span>Showing</span>
                <Select
                  name={'date_select'}
                  options={[
                    {
                      label: 'last 3 months',
                      value: '3_month',
                    },
                    {
                      label: 'last 6 months',
                      value: '6_month',
                    },
                  ]}
                  value={dateFilter}
                  onChange={(e) => {
                    e && setDateFilter({ label: e?.label, value: e?.value })
                  }}
                  styles={{
                    control: (base: CSSObjectWithLabel) => ({
                      ...base,
                      border: '2px',
                      minWidth: 185,
                      minHeight: 36,
                      fontFamily: 'Spartan',
                      fontWeight: 'bold',
                      fontSize: 12,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }),
                    valueContainer: (base: CSSObjectWithLabel) => ({
                      ...base,
                      minHeight: 36,
                    }),
                    indicatorsContainer: (base: CSSObjectWithLabel) => ({
                      ...base,
                      height: 36,
                    }),
                    menu: (base: CSSObjectWithLabel) => ({
                      ...base,
                      width: 'max-content',
                      minWidth: '100%',
                      fontFamily: 'Spartan',
                      fontWeight: 'bold',
                      fontSize: 12,
                    }),
                    dropdownIndicator: (base: CSSObjectWithLabel) => ({
                      ...base,
                      color: '#09121F', // Custom colour
                      marginTop: 0,
                    }),

                    clearIndicator: (base: CSSObjectWithLabel) => ({
                      ...base,
                      marginTop: 4,
                      color: '#09121F',
                    }),
                  }}
                />
              </div>
              <DropdownButton
                disabled={isLoadingCampaigns || !hasCampaigns}
                options={[
                  {
                    label: 'Excel',
                    onClick: async (e) => {
                      const toastId = toast.loading('Generating report...')

                      await mutateAsync({
                        metrics: METRICS.map((metric) => metric.value)?.filter(
                          (metric) => metric !== 'budget' || !!agency?.show_budget_type,
                        ),
                        conversions: tag_names?.map((conversion) => conversion.tag_name),
                        reportFormat: 'xlsx',
                        campaigns: uniqueCampaigns,
                        period:
                          dateFilter.value === '3_month'
                            ? 'last_3_months'
                            : 'last_6_months',
                        recurring: false,
                        recurring_interval: undefined,
                        email: user?.email,
                      })
                      toast.success(
                        'You will receive an email shortly with the report!',
                        { id: toastId, duration: 5000 },
                      )
                    },
                  },
                  {
                    label: 'PDF',
                    onClick: async (e) => {
                      e.preventDefault()
                      const toastId = toast.loading('Generating report...')
                      GA4.sendEvent({
                        event_name: 'export_pdf',
                        event_params: {
                          campaign_type: 'month_over_month',
                        },
                      })
                      mtm.sendEvent('Export', 'PDF', 'month_over_month')
                      await downloadReport(undefined, {
                        async onSuccess(data) {
                          toast.success('Report generated successfully', { id: toastId })
                          const blob = await data.blob()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = `${
                            dealership?.dealership
                          }_${dateFilter.label.replaceAll(' ', '_')}_campaigns.pdf`
                          document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                          a.click()
                          a.remove()
                        },
                      })
                    },
                  },
                ]}>
                Export
              </DropdownButton>
            </div>
          </div>
          <div className={'flex flex-col gap-8 mt-6'}>
            {isLoadingCampaigns && <LoadingComponent title={'Loading...'} />}
            {!isLoadingCampaigns && !hasCampaigns && (
              <FormProvider {...formContext}>
                <motion.form onSubmit={handleSubmit(onSubmit)}>
                  <div className="felx flex-col justify-center items-center">
                    <div className={'grid grid-cols-2 gap-5 h-fit px-12'}>
                      <CardRadioInput
                        icon={<IconCreate />}
                        name={'create_campaign_type'}
                        value={'new'}
                        label={'Create Campaigns'}
                        description={'Create a campaign from scratch'}
                        background={'white'}
                      />
                      <CardRadioInput
                        icon={<IconImport />}
                        name={'create_campaign_type'}
                        value={'import'}
                        label={'Import Campaigns'}
                        description={
                          <div>
                            <h3>
                              Import campaigns from your Google Ads, Facebook Ads, or
                              other sources
                            </h3>
                            <div
                              className={
                                'flex flex-row items-center justify-center gap-2 mt-4'
                              }>
                              <IconGoogleAds className={'w-8 h-8'} />
                              <IconFacebookAds className={'w-8 h-8'} />
                            </div>
                          </div>
                        }
                        background={'white'}
                      />
                    </div>
                    <div className="flex flex-row justify-center pt-5">
                      <Button variant={'primary'} type="submit" disabled={!isDirty}>
                        Continue
                      </Button>
                    </div>
                  </div>
                </motion.form>
              </FormProvider>
            )}
            {!isLoadingCampaigns && hasCampaigns && (
              <div>
                {uniqueCampaigns?.map(
                  ({ client_campaign_id, campaign_name, product_name, product_code }) => {
                    return (
                      <div key={client_campaign_id}>
                        <div className={'flex flex-col gap-1'}>
                          <div className={'flex gap-2 items-center pb-2'}>
                            <span className={'font-bold text-sm pb-1'}>
                              {campaign_name}
                            </span>
                            <Pill type={'gray'}>{product_name}</Pill>
                          </div>
                          <Table
                            columns={match(product_code)
                              .with('MOB', () => mobileColumns)
                              .with('DSP', () => [
                                ...displayColumns,
                                ...conversionColumns,
                              ])
                              .with('VLA', () => [
                                ...displayColumns,
                                ...conversionColumns,
                              ])
                              .with('FDS', () => displayColumns)
                              .with('AZD', () => displayColumns)
                              .with('SEM', () => searchColumns)
                              .with('MSE', () => searchColumns)
                              .with('DYD', () => [
                                ...displayWithSalesColumns,
                                ...conversionColumns,
                              ])
                              .with('INV', () => displayWithSalesColumns)
                              .with('DYN', () => displayWithSalesColumns)
                              .with('FDL', () => leadsWithSalesColumns)
                              .with('DSL', () => facebookLeadsColumns)
                              .with('OTT', () => [...videoColumns, ...conversionColumns])
                              .with('CTV', () => [...videoColumns, ...conversionColumns])
                              .with('TRV', () => videoColumns)
                              .with('VID', () => [...videoColumns, ...conversionColumns])
                              .with('AZV', () => videoColumns)
                              .with('DOH', () => audienceColumns)
                              .otherwise(() => baseColumns)}
                            filters={{
                              ...filters.dateRange,
                              campaign_ids: [{ value: client_campaign_id }],
                              onlyFlights: 1,
                            }}
                            hidePagination={true}
                            fetchFn={useGetCampaignsSummaryList}
                            isDownloadable={true}
                            tableName={`${campaign_name}_${dateFilter.value}`}
                          />
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Outlet />
    </>
  )
}

export default CampaignBreakdownReport
